import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, RefreshCw, Send, Users } from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import BannerImg from "../image/gallaryBanner.png";

const WORD_LIMIT = 400;

const countWords = (text) =>
  text.trim().split(/\s+/).filter(Boolean).length;

const memberDisplayName = (member = {}) =>
  member?.preferredName ||
  [member?.firstName, member?.lastName].filter(Boolean).join(" ").trim() ||
  member?.memberId ||
  "Member";

const MentorDashboard = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: "", message: "" });
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fetchedRef = useRef(false);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/mentor/assignments", { withCredentials: true });
      if (data.success) setAssignments(data.assignments || []);
    } catch (err) {
      setNotice({ type: "error", message: err?.response?.data?.message || "Could not load assignments." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchAssignments();
    }
  }, [fetchAssignments]);

  const openAssignment = async (assignment) => {
    setSelected(assignment);
    setMessageText("");
    setNotice({ type: "", message: "" });
    setMessagesLoading(true);
    try {
      const { data } = await axios.get(
        `/api/mentor/assignments/${assignment._id}/messages`,
        { withCredentials: true }
      );
      if (data.success) setMessages(data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSend = async () => {
    const text = messageText.trim();
    if (!text) return;
    const wc = countWords(text);
    if (wc > WORD_LIMIT) {
      setNotice({ type: "error", message: `Message must not exceed ${WORD_LIMIT} words. You used ${wc}.` });
      return;
    }

    setSubmitting(true);
    setNotice({ type: "", message: "" });
    try {
      const { data } = await axios.post(
        `/api/mentor/assignments/${selected._id}/messages`,
        { message: text },
        { withCredentials: true }
      );
      if (data.success) {
        setMessages((prev) => [data.mentorMessage, ...prev]);
        setMessageText("");
        setNotice({ type: "success", message: "Message sent." });
      }
    } catch (err) {
      setNotice({ type: "error", message: err?.response?.data?.message || "Failed to send message." });
    } finally {
      setSubmitting(false);
    }
  };

  const wc = countWords(messageText);
  const remaining = WORD_LIMIT - wc;
  const isOverLimit = wc > WORD_LIMIT;

  return (
    <div>
      <Header />
      <Banner backgroundImage={BannerImg} heading="Mentor Dashboard" minHeight="40vh" padding="80px 0" />

      <section className="main-lwbl rounded-[30px] bg-[#F9FAFB] px-4 py-16">
        <div className="mx-auto max-w-5xl">

          <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                <Users size={24} />
              </div>
              <div>
                <p className="font-outfit text-sm font-semibold uppercase tracking-[0.18em] text-cyan-600">
                  Mentor workspace
                </p>
                <h2 className="mt-1 font-cinzel text-2xl font-bold text-gray-900">My Mentees</h2>
                <p className="mt-1 font-outfit text-sm text-gray-500">
                  Select a mentee to view history and send messages (max {WORD_LIMIT} words).
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchAssignments}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-outfit text-sm font-semibold text-gray-700 hover:border-cyan-300 hover:text-cyan-700"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
          </div>

          {notice.message && (
            <div className={`mb-5 rounded-2xl border px-4 py-3 font-outfit text-sm ${
              notice.type === "error"
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}>
              {notice.message}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-cyan-400 border-t-transparent" />
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              {/* Mentee list */}
              <div className="md:col-span-1">
                <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
                  <div className="border-b border-gray-100 px-4 py-3">
                    <p className="font-outfit text-sm font-semibold text-gray-700">
                      {assignments.length} mentee{assignments.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  {assignments.length === 0 ? (
                    <div className="px-4 py-10 text-center">
                      <Users size={28} className="mx-auto mb-2 text-gray-300" />
                      <p className="font-outfit text-sm text-gray-400">No mentees assigned yet.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {assignments.map((a) => {
                        const m = a.member || {};
                        const isActive = selected?._id === a._id;
                        return (
                          <button
                            key={a._id}
                            type="button"
                            onClick={() => openAssignment(a)}
                            className={`w-full px-4 py-3 text-left transition-colors ${
                              isActive ? "bg-cyan-50" : "hover:bg-gray-50"
                            }`}
                          >
                            <p className={`font-outfit text-sm font-semibold ${isActive ? "text-cyan-700" : "text-gray-800"}`}>
                              {memberDisplayName(m)}
                            </p>
                            <p className="font-outfit text-xs text-gray-400">{m.memberId || m.email || ""}</p>
                            {a.lastMessage && (
                              <p className="mt-1 truncate font-outfit text-xs text-gray-400">
                                {a.lastMessage.message}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Message panel */}
              <div className="md:col-span-2">
                {!selected ? (
                  <div className="flex h-full min-h-[300px] items-center justify-center rounded-3xl border border-gray-100 bg-white shadow-sm">
                    <div className="text-center">
                      <MessageSquare size={32} className="mx-auto mb-3 text-gray-300" />
                      <p className="font-outfit text-sm text-gray-400">Select a mentee to start messaging.</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
                    <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
                      <p className="font-cinzel text-base font-bold text-gray-900">
                        {memberDisplayName(selected.member)}
                      </p>
                      <p className="font-outfit text-xs text-gray-400">
                        {selected.member?.memberId || selected.member?.email || ""}
                      </p>
                    </div>

                    {/* History */}
                    <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 px-5">
                      {messagesLoading ? (
                        <div className="flex justify-center py-8">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
                        </div>
                      ) : messages.length === 0 ? (
                        <p className="py-8 text-center font-outfit text-sm text-gray-400">No messages yet.</p>
                      ) : (
                        messages.map((msg) => (
                          <div key={msg._id} className="py-3">
                            <p className="font-outfit text-sm leading-6 text-gray-700">{msg.message}</p>
                            <p className="mt-1 font-outfit text-xs text-gray-400">
                              {new Date(msg.createdAt).toLocaleString()} · {msg.wordCount} words
                            </p>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Compose */}
                    <div className="border-t border-gray-100 px-5 py-4">
                      <textarea
                        rows={4}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder="Write your message..."
                        className={`w-full rounded-2xl border px-4 py-3 font-outfit text-sm leading-6 outline-none transition-colors ${
                          isOverLimit
                            ? "border-red-300 bg-red-50 focus:border-red-400"
                            : "border-gray-200 bg-gray-50 focus:border-cyan-300 focus:bg-white"
                        }`}
                      />
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className={`font-outfit text-xs ${isOverLimit ? "font-semibold text-red-600" : "text-gray-400"}`}>
                          {isOverLimit ? `${Math.abs(remaining)} words over limit` : `${remaining} words remaining`}
                        </span>
                        <span className="font-outfit text-xs text-gray-400">{wc} / {WORD_LIMIT}</span>
                      </div>
                      <button
                        type="button"
                        disabled={isOverLimit || submitting || !messageText.trim()}
                        onClick={handleSend}
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {submitting
                          ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          : <Send size={14} />}
                        Send message
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MentorDashboard;
